import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getAllAlarmsXYWithIdProject } from 'services/api';
import DataGrid from '../../UI/DataGrid/DataGrid';
import Toolbar from '../../UI/Toolbar/Toolbar';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as privilege from '../../../ressources/glossaires/glossaire_privileges';
import * as requestType from '../../../ressources/glossaires/glossaire_requetes';
import S from './TableauAlarmeXY.module.scss';

// Column definitions factory
const createColumn = (field, labelKey, width, flex) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  ...(flex && width && { minWidth: width }),
  ...(flex && { flex }),
  sortable: true,
  align: 'center',
  headerAlign: 'center',
});

// [field, labelKey, width, flex]
const COLUMN_CONFIG = [
  ['alarm_xy_id', 'id', 110],
  ['project_id', 'id_projet', 120],
  ['template_id', 'template_id', 140],
  ['Xsensor_id', 'x_sensor_id', 140],
  ['Xcomponent_id', 'x_component_id', 160, 1],
  ['Ysensor_id', 'y_sensor_id', 140],
  ['Ycomponent_id', 'y_component_id', 160, 1],
];

const TableauAlarmeXY = (props) => {
  const {
    creer,
    projetSelectione,
    children,
    interfaceAdmin,
    filtre_projects_list_id,
    project_id_url,
    privilegesList = [],
  } = props;

  const [alarmListXY, setAlarmListXY] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const canCreate = useMemo(
    () => privilegesList.includes(privilege.creer_des_alarmes),
    [privilegesList]
  );

  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, labelKey, width, flex]) =>
        createColumn(field, labelKey, width, flex)
      ),
    []
  );

  const fetchAlarms = useCallback(
    async (projectIds) => {
      const payload = { list_project_id: [] };

      if (projectIds && projectIds.length) {
        payload.list_project_id = projectIds;
      } else if (!isNaN(project_id_url) && project_id_url !== '') {
        payload.list_project_id = [parseInt(project_id_url)];
      } else if (filtre_projects_list_id?.length) {
        payload.list_project_id = filtre_projects_list_id;
      }

      if (payload.list_project_id.length === 0) {
        setAlarmListXY([]);
        return;
      }

      try {
        const alarmList = await getAllAlarmsXYWithIdProject(payload);
        if (alarmList.data.state) {
          setAlarmListXY(alarmList.data.list_alarms_xy);
        } else {
          console.error(
            'Erreur lors de la récupération des alarmes XY:',
            alarmList.data.errors
          );
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des alarmes XY:', error);
      }
    },
    [filtre_projects_list_id, project_id_url]
  );

  useEffect(() => {
    fetchAlarms();
  }, [fetchAlarms]);

  const handleRefresh = useCallback(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    fetchAlarms(projectIds);
  }, [fetchAlarms, filtre_projects_list_id, project_id_url]);

  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      const selectedRow = alarmListXY.find(
        (row) => row.alarm_xy_id === newSelection[0]
      );

      if (selectedRow) {
        projetSelectione(selectedRow);
      }
    },
    [alarmListXY, projetSelectione]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  const filteredRows = useMemo(() => {
    if (!alarmListXY) return [];

    return alarmListXY.filter((alarm) => {
      const projectMatch = interfaceAdmin
        ? filtre_projects_list_id?.includes(parseInt(alarm.project_id))
        : parseInt(alarm.project_id) === parseInt(project_id_url);

      if (!projectMatch) return false;
      if (!searchValue) return true;

      const searchLower = searchValue.toLowerCase();
      return Object.values(alarm).some((value) =>
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }, [
    alarmListXY,
    interfaceAdmin,
    filtre_projects_list_id,
    project_id_url,
    searchValue,
  ]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: 25, page: 0 } },
      sorting: { sortModel: [{ field: 'alarm_xy_id', sort: 'desc' }] },
    }),
    []
  );

  return (
    <div className={S.container}>
      <Toolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchPlaceholder="recherche"
        onCreate={canCreate ? creer : undefined}
        createTooltip={checkMot('creer')}
        onRefresh={handleRefresh}
        refreshRequestType={
          requestType.recuperer_la_liste_des_configurations_des_alarmes_xy
        }
        refreshMessage={checkMot('maj_alarme_xy_en_cour')}
      >
        {children}
      </Toolbar>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.alarm_xy_id}
        onRowSelectionModelChange={handleSelectionModelChange}
        rowSelectionModel={selectionModel}
        disableMultipleRowSelection
        pageSizeOptions={[25, 50, 100]}
        initialPageSize={25}
        autoRowHeight
        initialState={initialState}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    interfaceAdmin: state.authentifie.interfaceAdmin,
    filtre_projects_list_id: state.authentifie.filtre_projects_list_id,
    project_id_url: state.interfaceClient.project_id_url,
    privilegesList: state.authentifie.privileges_list || [],
  };
};

export default connect(mapStateToProps)(TableauAlarmeXY);
